import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import styled, { css } from 'styled-components';

import CalmIcon from 'assets/effects/calm';
import ClearMindIcon from 'assets/effects/clear-mind';
import CreativeIcon from 'assets/effects/creative';
import EnergeticIcon from 'assets/effects/energetic';
import FocusedIcon from 'assets/effects/focused';
import HappyIcon from 'assets/effects/happy';
import InspiredIcon from 'assets/effects/inspired';
import RelaxedIcon from 'assets/effects/relaxed';
import SleepyIcon from 'assets/effects/sleepy';
import UpliftedIcon from 'assets/effects/uplifted';

import { REVIEW_EFFECTS, ReviewEffect, getEffectLabel } from './effects';

/** Shared effect SVGs from `assets/effects/*` — the same icons the product-details Effects section renders. */
type EffectSvg = React.ComponentType<{ height?: string }>;

const EFFECT_ICONS: Record<ReviewEffect, EffectSvg> = {
  calm: CalmIcon,
  clearMind: ClearMindIcon,
  creative: CreativeIcon,
  energetic: EnergeticIcon,
  focused: FocusedIcon,
  happy: HappyIcon,
  inspired: InspiredIcon,
  relaxed: RelaxedIcon,
  sleepy: SleepyIcon,
  uplifted: UpliftedIcon,
};

type EffectsChipsInputProps = {
  /** react-hook-form field name; value is a `ReviewEffect[]` */
  name: string;
  disabled?: boolean;
};

/**
 * Optional multi-select chips for product review effects. Integrates with
 * react-hook-form via `Controller`; the field value is a `ReviewEffect[]`.
 */
export const EffectsChipsInput = ({ name, disabled = false }: EffectsChipsInputProps): JSX.Element => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ value, onChange }): JSX.Element => {
        const selected: ReviewEffect[] = value ?? [];

        const toggle = (effect: ReviewEffect): void => {
          if (selected.includes(effect)) {
            onChange(selected.filter((item) => item !== effect));
          } else {
            onChange([...selected, effect]);
          }
        };

        return (
          <ChipList role='group' aria-label='How did it make you feel?'>
            {REVIEW_EFFECTS.map((effect) => {
              const isSelected = selected.includes(effect);
              const Icon = EFFECT_ICONS[effect];
              return (
                <Chip
                  key={effect}
                  type='button'
                  $selected={isSelected}
                  aria-pressed={isSelected}
                  disabled={disabled}
                  onClick={() => toggle(effect)}
                >
                  <Icon height='16px' />
                  {getEffectLabel(effect)}
                </Chip>
              );
            })}
          </ChipList>
        );
      }}
    />
  );
};

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 18px;
  border: 1px solid ${({ theme }) => theme.colors.grey[80]};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.grey[30]};
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  svg {
    flex-shrink: 0;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.blue[55]};
    outline-offset: 1px;
  }

  ${({ $selected, theme }) =>
    $selected &&
    css`
      background: var(--dct--customized-colors-buttonsLinks);
      border-color: var(--dct--customized-colors-buttonsLinks);
      color: ${theme.colors.white};
    `}
`;
